import type { Middleware } from "webamp";
import type { LiveTrack } from "./metadata-sync";

export interface AudibleTrackAnchor {
	duration: number;
	elapsedAtSync: number;
	identity: string;
	syncedAt: number;
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(maximum, Math.max(minimum, value));
}

export function liveTrackIdentity(track: LiveTrack): string {
	const contentId = String(
		track.id || `${track.artist || ""}:${track.title || ""}`,
	);
	return `${contentId}:${track.playedAt || 0}`;
}

export class AudibleTrackClock {
	#anchor: AudibleTrackAnchor | null = null;

	clear(): void {
		this.#anchor = null;
	}

	getAnchor(): AudibleTrackAnchor | null {
		return this.#anchor ? { ...this.#anchor } : null;
	}

	setTrack(track: LiveTrack, elapsedAtSync: number, syncedAt: number): void {
		const duration = Math.max(0, Number(track.duration) || 0);
		const identity = liveTrackIdentity(track);
		let elapsed = clamp(Number(elapsedAtSync) || 0, 0, duration);
		if (this.#anchor?.identity === identity) {
			elapsed = Math.max(elapsed, this.elapsed(syncedAt));
		}
		this.#anchor = { duration, elapsedAtSync: elapsed, identity, syncedAt };
	}

	elapsed(at = Date.now()): number {
		const anchor = this.#anchor;
		if (!anchor) return 0;
		const secondsSinceSync = Math.max(0, at - anchor.syncedAt) / 1_000;
		return Math.floor(
			clamp(anchor.elapsedAtSync + secondsSinceSync, 0, anchor.duration),
		);
	}
}

export function createAudibleClockMiddleware(
	clock: AudibleTrackClock,
	currentTrackId: () => number | null | undefined,
	now: () => number = Date.now,
): Middleware {
	return () => (next) => (action) => {
		const anchor = clock.getAnchor();
		if (anchor && action.type === "UPDATE_TIME_ELAPSED") {
			return next({ ...action, elapsed: clock.elapsed(now()) });
		}
		if (
			anchor &&
			action.type === "SET_MEDIA" &&
			action.id === currentTrackId()
		) {
			return next({ ...action, length: anchor.duration });
		}
		return next(action);
	};
}
